import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type CurrentUser } from '../api/client.js';

/**
 * Current session state.
 *
 * `/api/auth/me` is the single source of truth: a 200 means fully signed in, a
 * 403 carries a code saying what is still outstanding, and a 401 means signed
 * out. Deriving the state from one endpoint keeps the client from inventing its
 * own idea of what a half-authenticated session is allowed to do.
 */

export type SessionState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  /** Password accepted; an authenticator still has to be enrolled. */
  | { status: 'needsEnrollment' }
  /** Enrolled; a code from the authenticator is required. */
  | { status: 'needsCode' }
  | { status: 'needsPasswordChange' }
  | {
      status: 'signedIn';
      user: CurrentUser;
      unusedRecoveryCodes: number;
      /** The zone the business keeps its calendar in, from the server. */
      businessTimeZone: string;
    }
  | { status: 'unreachable' };

interface MeResponse {
  user: CurrentUser;
  unusedRecoveryCodes: number;
  businessTimeZone: string;
}

/**
 * How often to re-ask who is signed in, or `false` to not poll at all.
 *
 * Only a query that has succeeded is polled, which means only a signed-in
 * window. A query that has never succeeded goes back to `pending` each time it
 * refetches, and the shell renders `pending` as the loading spinner: polling
 * from the sign-in, enrollment, or code screen unmounted the form every thirty
 * seconds and threw away whatever had been typed or scanned. Those screens
 * refresh the session themselves when their step completes, so they have
 * nothing to poll for.
 */
export function sessionPollInterval(queryStatus: 'pending' | 'error' | 'success'): number | false {
  return queryStatus === 'success' ? 30_000 : false;
}

export function useSession(): SessionState {
  const query = useQuery<MeResponse, ApiError>({
    queryKey: ['session'],
    queryFn: () => api.get<MeResponse>('/api/auth/me'),
    retry: false,
    staleTime: 30_000,
    // Re-asked on a timer so a window left open finds out within half a
    // minute that its session ended — an administrator deactivating the
    // account, say — rather than only when its user next clicks something.
    // It is the cheapest query the API has.
    refetchInterval: (current) => sessionPollInterval(current.state.status),
  });

  if (query.isPending) return { status: 'loading' };

  // Error is checked before data on purpose. After a sign-out the refetch
  // fails with a 401, but the query keeps the data from its last successful
  // fetch, so trusting data first would leave the app showing a signed-in
  // shell for a session the server has already ended.
  const error = query.error;

  if (!error && query.data) {
    return {
      status: 'signedIn',
      user: query.data.user,
      unusedRecoveryCodes: query.data.unusedRecoveryCodes,
      businessTimeZone: query.data.businessTimeZone,
    };
  }

  if (!error) return { status: 'signedOut' };

  if (error.status === 0) return { status: 'unreachable' };

  if (error.status === 403) {
    if (error.code === 'ENROLLMENT_REQUIRED') return { status: 'needsEnrollment' };
    if (error.code === 'MFA_REQUIRED') return { status: 'needsCode' };
    if (error.code === 'PASSWORD_CHANGE_REQUIRED') return { status: 'needsPasswordChange' };
  }

  return { status: 'signedOut' };
}

/** Forces the session to be re-read, after signing in or out. */
export function useRefreshSession(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['session'] });
  };
}
